/**
 * Die Aggregatzellen der Sammelsendungs-Elternzeile (Plan 2026-10-02-sammelsendung-aggregat-zeile).
 *
 * `aggregiereSpalte` (reine Funktion, `sammelsendungAggregat.ts`) entscheidet, WAS in einer Spalte
 * steht; hier wird es mit denselben Bausteinen gezeichnet wie in der Kinderzeile (`Datum`, Pillen aus
 * `Bestellzeile.tsx`). Die Zellen sind NUR LESEND: keine `Bearbeitbar`-Huelle, kein Editor, kein
 * Klick-Handler - die Zeile reagiert allein ueber ihre zwei Knoepfe (`Sammelsendungszeile.tsx`). Die Faelligkeitszelle
 * wird erst in `BoardSeite` mit `SammelFaelligkeitZelle` umhuellt (Plan 2026-10-31, Task 6); was hier entsteht, bleibt
 * die reine Anzeige, die diese Huelle als `inhalt` bekommt.
 *
 * Zentrale Werte der Sendung (Lieferant, Status, Gesamtbetrag) kommen fertig aus `BoardSeite`; sie
 * tragen die bisherigen Klassen `sammelsendung-lieferant/-status/-betrag`.
 */
import type { ReactNode } from "react";
import { fmtDate } from "../../lib/format.ts";
import type { StatusDefinitionen } from "../../lib/status.ts";
import type { Order } from "../../lib/types.ts";
import { Datum, EingebuchtPille, StatusPille, ZahlungPille, ZollPille } from "./Bestellzeile.tsx";
import { aggregiereSpalte } from "./sammelsendungAggregat.ts";
import type { Aggregat } from "./sammelsendungAggregat.ts";

export interface ZentraleWerte {
  /** Bereits aufgeloester Lieferant bzw. "Lieferant gemischt"; `null` = nichts zeigen. */
  lieferant: string | null;
  /** Anzeigetext des zentralen Status bzw. "Status gemischt"/"Ohne Status". */
  statusLabel: string;
  /** Id des zentralen Status, wenn er einheitlich gesetzt ist - dann steht die Statuspille da. */
  statusId: string | null;
  gesamtbetrag: string;
  /** Wie `gesamtbetrag`, mehrere Waehrungen mit " / " getrennt (der Kopf trennt seine Teile mit "·"). */
  gesamtbetragKopf?: string;
  gesamtbetragManuell: boolean;
  /** Zentrales Faelligkeitsdatum der Sendung ("YYYY-MM-DD"); `null`: keins, die Zelle leitet aus den Kindern ab. */
  faelligkeit: string | null;
}

function Leer() {
  return <span className="order-list-empty">–</span>;
}

function AggregatInhalt({ aggregat, schluessel }: { aggregat: Aggregat; schluessel: string }) {
  switch (aggregat.art) {
    case "leer":
      return <Leer />;
    case "gemischt":
    case "zentral":
      return null;
    case "text":
      return <span title={aggregat.titel}>{aggregat.text}</span>;
    case "datum":
      // Die Faelligkeit wird wie in der Kinderzeile als ueberfaellig/heute markiert.
      return (
        <span title={aggregat.titel}>
          <Datum wert={aggregat.datum} markDue={schluessel === "due"} />
        </span>
      );
    case "bereich":
      return <span className="order-list-date">{`${fmtDate(aggregat.von)} – ${fmtDate(aggregat.bis)}`}</span>;
    case "pille":
      return aggregat.pille === "zoll" ? <ZollPille /> : <ZahlungPille />;
    case "eingebucht":
      return <EingebuchtPille zustand={aggregat.zustand} datum={aggregat.datum} titel={aggregat.titel} />;
  }
}

/** Die drei zentralen Werte, die im Kopf der Elternzeile einspringen, wenn ihre Spalte fehlt. */
export type KopfWerte = Partial<Record<"supplier" | "status" | "value", ReactNode>>;

function zentraleInhalte(opts: { definitionen: StatusDefinitionen; zentral: ZentraleWerte }, betrag: string): {
  supplier: ReactNode;
  status: ReactNode;
  value: ReactNode;
} {
  const { zentral } = opts;
  return {
    supplier: zentral.lieferant ? (
      <span className="sammelsendung-lieferant" title={zentral.lieferant}>
        {zentral.lieferant}
      </span>
    ) : null,
    status: (
      <span className="sammelsendung-status">
        {zentral.statusId ? (
          <StatusPille definitionen={opts.definitionen} status={zentral.statusId} />
        ) : (
          zentral.statusLabel
        )}
      </span>
    ),
    value: (
      <span
        className="sammelsendung-betrag"
        title={zentral.gesamtbetragManuell ? "Manuell festgelegter Gesamtbetrag" : "Summe der Zahlungspläne"}
      >
        {betrag}
        {zentral.gesamtbetragManuell ? <span className="sammelsendung-betrag-manuell"> (manuell)</span> : null}
      </span>
    ),
  };
}

/**
 * Je Spaltenschluessel der Inhalt der Aggregatzelle (`zellen`) und die zentralen Werte fuer den Kopf
 * (`kopf`): die Elternzeile zeigt Lieferant, Status und Betrag dort, wo ihre Spalte nicht als eigene
 * Zelle sichtbar ist (ausgeblendet oder selbst die erste Spalte) - nie doppelt.
 */
export function baueAggregatZellen(opts: {
  schluessel: readonly string[];
  kinder: readonly Order[];
  dsvShipmentNo: string;
  definitionen: StatusDefinitionen;
  zentral: ZentraleWerte;
}): { zellen: Record<string, ReactNode>; kopf: KopfWerte } {
  const zentral = zentraleInhalte(opts, opts.zentral.gesamtbetrag);
  const kopf = zentraleInhalte(opts, opts.zentral.gesamtbetragKopf ?? opts.zentral.gesamtbetrag);
  const zellen: Record<string, ReactNode> = {};
  for (const schluessel of opts.schluessel) {
    const aggregat = aggregiereSpalte(schluessel, opts.kinder, opts.dsvShipmentNo, opts.zentral.faelligkeit);
    if (aggregat.art !== "zentral") {
      zellen[schluessel] = <AggregatInhalt aggregat={aggregat} schluessel={schluessel} />;
    } else if (schluessel === "supplier") {
      zellen[schluessel] = zentral.supplier ?? <Leer />;
    } else if (schluessel === "status") {
      zellen[schluessel] = zentral.status;
    } else if (schluessel === "value") {
      zellen[schluessel] = zentral.value;
    }
  }
  return { zellen, kopf };
}
